import React, { useMemo } from 'react';
import PropTypes from 'prop-types';

import ScheduleListShift from './ScheduleListShift';
import { getDayKey, getWeekStart, getWeekEnd } from './scheduleHelpers';

const daysInRange = (startDate, endDate) => {
  const count = Math.max(0, Math.round(endDate.diff(startDate, 'days').as('days')));
  return new Array(count).fill(1).map((_, idx) => startDate.plus({ days: idx }));
};

const rangeLabel = (viewType, startDate, endDate) => {
  if (viewType === 'Month') return startDate.toFormat('LLLL yyyy');
  if (startDate.hasSame(endDate, 'day')) return startDate.toFormat('LLL d, yyyy');
  if (startDate.hasSame(endDate, 'month')) {
    return `${startDate.toFormat('LLL d')}–${endDate.toFormat('d, yyyy')}`;
  }
  if (startDate.hasSame(endDate, 'year')) {
    return `${startDate.toFormat('LLL d')} – ${endDate.toFormat('LLL d, yyyy')}`;
  }
  return `${startDate.toFormat('LLL d, yyyy')} – ${endDate.toFormat('LLL d, yyyy')}`;
};

const viewRange = (date, viewType) => {
  if (viewType === 'Week') return { startDate: getWeekStart(date), endDate: getWeekEnd(date) };
  if (viewType === 'Month') return { startDate: date.startOf('month'), endDate: date.endOf('month') };
  return { startDate: date.startOf('day'), endDate: date.endOf('day') };
};

function ScheduleDayList({
  date,
  viewType,
  shifts,
  onShiftSelect,
}) {
  const { groups, total } = useMemo(() => {
    const { startDate, endDate } = viewRange(date, viewType);
    const seen = new Set();
    const nextGroups = daysInRange(startDate, endDate).map((day) => {
      const dayShifts = (shifts[getDayKey(day)] || [])
        .slice()
        .sort((a, b) => a.startTime - b.startTime);
      dayShifts.forEach((shift) => {
        if (shift.id) seen.add(shift.id);
      });
      return { day, shifts: dayShifts };
    }).filter((group) => group.shifts.length);
    return { groups: nextGroups, total: seen.size, startDate, endDate };
  }, [date, viewType, shifts]);

  const { startDate, endDate } = viewRange(date, viewType);

  return (
    <div className="schedule-list-view">
      <div className="schedule-list-summary">
        <div className="schedule-list-summary-range">{rangeLabel(viewType, startDate, endDate)}</div>
        <div className="schedule-list-count">{`${total} shift${total === 1 ? '' : 's'}`}</div>
      </div>
      {groups.map((group) => (
        <section key={group.day.toSQLDate()} className="schedule-list-day">
          <div className="schedule-list-day-header">
            <div className="schedule-list-day-badge">
              <span>{group.day.toFormat('ccc').toUpperCase()}</span>
              <span>{group.day.day}</span>
            </div>
            <div>
              <div className="schedule-list-day-name">{group.day.toFormat('cccc')}</div>
              <div className="schedule-list-day-sub">
                {`${group.day.toFormat('LLL d')} · `}
                <span className="schedule-list-shift-count">
                  {`${group.shifts.length} shift${group.shifts.length === 1 ? '' : 's'}`}
                </span>
              </div>
            </div>
          </div>
          {group.shifts.map((shift) => (
            <ScheduleListShift
              key={shift.id}
              shift={shift}
              onSelect={onShiftSelect}
            />
          ))}
        </section>
      ))}
      {total === 0 && <div className="schedule-list-empty">No shifts</div>}
    </div>
  );
}

ScheduleDayList.propTypes = {
  date: PropTypes.object.isRequired,
  viewType: PropTypes.string.isRequired,
  shifts: PropTypes.object.isRequired,
  onShiftSelect: PropTypes.func.isRequired,
};

export default ScheduleDayList;
